import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { addMaterials, getMeasurementUnitsList } from "../../../../../services/Admin/MaterialsApi";

const priorityOptions = [
  { value: "A", label: "تصنيف أول" },
  { value: "B", label: "تصنيف ثاني" },
  { value: "C", label: "تصنيف ثالث" },
  { value: "D", label: "تصنيف رابع" },
  { value: "Z", label: "تصنيف خامس" },
];

const defaultMaterial = () => ({
  name: "",
  name_en: "",
  measurment_unit_id: "",
  priority: "A",
  cost_price_per_unit: "",
  status: true,
  max_usage_per_product: "",
  keywords: "",
  rules: [""],
});

const normalizeKeywords = (value) => String(value || "")
  .split(/[\s,،;؛]+/)
  .map((keyword) => keyword.trim())
  .filter(Boolean);

const normalizeRules = (value) => {
  if (Array.isArray(value)) {
    return value.map((rule) => String(rule).trim()).filter(Boolean);
  }

  return String(value || "")
    .split(/\r?\n/)
    .map((rule) => rule.trim())
    .filter(Boolean);
};

const AddMaterialsDialog = ({ open, onClose, onAdded }) => {
  const [rows, setRows] = useState([defaultMaterial()]);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchUnits = async () => {
      setLoadingUnits(true);
      try {
        const res = await getMeasurementUnitsList();
        setUnits(res?.data || []);
      } catch (err) {
        // ignore, units optional
      } finally {
        setLoadingUnits(false);
      }
    };

    if (open) fetchUnits();
  }, [open]);

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleRuleChange = (materialIndex, ruleIndex, value) => {
    setRows((prev) => prev.map((row, index) => {
      if (index !== materialIndex) return row;

      const nextRules = [...(Array.isArray(row.rules) ? row.rules : [""])];
      nextRules[ruleIndex] = value;

      return { ...row, rules: nextRules };
    }));
  };

  const addRule = (materialIndex) => {
    setRows((prev) => prev.map((row, index) => (
      index === materialIndex
        ? { ...row, rules: [...(Array.isArray(row.rules) ? row.rules : []), ""] }
        : row
    )));
  };

  const removeRule = (materialIndex, ruleIndex) => {
    setRows((prev) => prev.map((row, index) => {
      if (index !== materialIndex) return row;

      const nextRules = (Array.isArray(row.rules) ? row.rules : [""]).filter((_, idx) => idx !== ruleIndex);

      return { ...row, rules: nextRules.length ? nextRules : [""] };
    }));
  };

  const addRow = () => setRows((prev) => [...prev, defaultMaterial()]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const reset = () => {
    setRows([defaultMaterial()]);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const materials = rows
        .map((r) => ({
          name: String(r.name || "").trim(),
          name_en: String(r.name_en || "").trim(),
          measurment_unit_id: Number(r.measurment_unit_id) || null,
          priority: r.priority || "A",
          cost_price_per_unit: Number(r.cost_price_per_unit) || 0,
          status: Boolean(r.status),
          stock_quantity: null,
          max_usage_per_product: Number(r.max_usage_per_product) || 0,
          min_stock_threshold: null,
          keywords: normalizeKeywords(r.keywords),
          rules: normalizeRules(r.rules),
        }))
        .filter((m) => m.name);

      if (!materials.length) {
        setError("الرجاء إدخال خامة واحدة على الأقل");
        setLoading(false);
        return;
      }

      const res = await addMaterials(materials);
      setSuccess(res?.message || "تمت الإضافة بنجاح");
      if (typeof onAdded === "function") onAdded(res?.data || null);
      reset();
    } catch (err) {
      setError(err?.message || "فشل في إضافة الخامات");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    if (typeof onClose === "function") onClose();
  };

  return (
    <Dialog open={Boolean(open)} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>إضافة خامات</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {rows.map((r, idx) => (
            <Box
              key={idx}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField label="الاسم" value={r.name} onChange={(e) => handleChange(idx, "name", e.target.value)} required />
              <TextField label="الاسم بالإنجليزي" value={r.name_en} onChange={(e) => handleChange(idx, "name_en", e.target.value)} />

              <FormControl fullWidth>
                <InputLabel id={`unit-label-${idx}`}>وحدة القياس</InputLabel>
                <Select labelId={`unit-label-${idx}`} value={r.measurment_unit_id || ""} label="وحدة القياس" onChange={(e) => handleChange(idx, "measurment_unit_id", e.target.value)}>
                  <MenuItem value="">اختر وحدة</MenuItem>
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id={`priority-label-${idx}`}>التصنيف</InputLabel>
                <Select
                  labelId={`priority-label-${idx}`}
                  value={r.priority || "A"}
                  label="التصنيف"
                  onChange={(e) => handleChange(idx, "priority", e.target.value)}
                >
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField label="سعر الوحدة" value={r.cost_price_per_unit} onChange={(e) => handleChange(idx, "cost_price_per_unit", e.target.value)} />
              <TextField label="أقصى استخدام لكل منتج" value={r.max_usage_per_product} onChange={(e) => handleChange(idx, "max_usage_per_product", e.target.value)} />

              <TextField label="الكلمات المفتاحية (مفصولة بفواصل)" value={r.keywords} onChange={(e) => handleChange(idx, "keywords", e.target.value)} fullWidth />
              <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, display: "grid", gap: 1 }}>
                {(Array.isArray(r.rules) ? r.rules : [""]).map((rule, ruleIndex) => (
                  <Box key={`${idx}-${ruleIndex}`} sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 1, alignItems: "center" }}>
                    <Box sx={{ fontWeight: 800, color: "text.secondary", minWidth: 28, textAlign: "center" }}>
                      {ruleIndex + 1}
                    </Box>
                    <TextField
                      label={`قاعدة ${ruleIndex + 1}`}
                      value={rule}
                      onChange={(e) => handleRuleChange(idx, ruleIndex, e.target.value)}
                      fullWidth
                    />
                    <IconButton aria-label="remove rule" onClick={() => removeRule(idx, ruleIndex)} disabled={(r.rules || []).length === 1}>
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
                <Box>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addRule(idx)}>
                    إضافة قاعدة
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel control={<Switch checked={Boolean(r.status)} onChange={(e) => handleChange(idx, "status", e.target.checked)} />} label="متاح" />
                <IconButton aria-label="remove" onClick={() => removeRow(idx)} disabled={rows.length === 1}>
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            </Box>
          ))}

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button startIcon={<AddIcon />} onClick={addRow}>أضف صف</Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={handleClose} color="inherit">إلغاء</Button>
            <Button type="submit" variant="contained" disabled={loading || loadingUnits}>
              {loading ? <CircularProgress size={20} /> : "حفظ"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddMaterialsDialog;
